import React, {useRef, useState} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {EnrichedAstNode} from 'rendition-core';
import {CSSProperties} from 'styled-components';

interface LayerProps {
    node: EnrichedAstNode;
    moveLayer: (fromNodeId: string, toNodeId: string, position: 'top' | 'bottom' | 'middle' | null) => void;
    children?: React.ReactNode;
}

const Layer: React.FC<LayerProps> = ({node, moveLayer, children}) => {
    const ref = useRef<HTMLDivElement>(null);
    const hoverPosition = useRef<'top' | 'middle' | 'bottom' | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [shouldUpdate, setShouldUpdate] = useState(false);

    const [{canDrop, isOver}, drop] = useDrop(() => ({
        accept: 'layer',
        drop: (item: {node: EnrichedAstNode}, monitor) => {
            if (!monitor.didDrop()) {
                moveLayer(item.node.internalId, node.internalId, hoverPosition.current);
            }
        },
        hover: (item: {node: EnrichedAstNode}, monitor) => {
            const isDifferentItem = item?.node.internalId !== node.internalId;
            if (!isDifferentItem) {
                return;
            }

            // Get mouse cursor position and calculate relative position within the layer
            const clientOffset = monitor.getClientOffset();
            const layerRect = ref.current?.getBoundingClientRect();
            let position: 'top' | 'middle' | 'bottom' = 'middle';
            if (clientOffset && layerRect) {
                const relativeY = clientOffset.y - layerRect.top;
                const layerHeight = layerRect.height;
                if (node.type === 'Frame') {
                    if (relativeY <= layerHeight * 0.25) {
                        position = 'top';
                    } else if (relativeY >= layerHeight * 0.75) {
                        position = 'bottom';
                    }
                } else {
                    if (relativeY <= layerHeight * 0.5) {
                        position = 'top';
                    } else {
                        position = 'bottom';
                    }
                }
            }

            if (hoverPosition.current !== position) {
                hoverPosition.current = position;
                setShouldUpdate((prev) => !prev);
            }
        },
        collect: (monitor) => {
            const isOverCurrent = monitor.isOver({shallow: true});
            const draggedItem = monitor.getItem();

            // Check if the dragged item is different from the current item
            const isDifferentItem = draggedItem?.node.internalId !== node.internalId;

            return {
                isOver: isOverCurrent && isDifferentItem,
                canDrop: monitor.canDrop() && isDifferentItem,
            };
        },
    }));

    const [{isDragging}, drag] = useDrag({
        type: 'layer',
        item: {node: node},
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(drop(ref));

    let separatorTopStyle: CSSProperties = {
        position: 'absolute',
        top: -1,
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: 'rgba(256, 256, 256, 0.12)',
    };

    let separatorBottomStyle: CSSProperties = {
        position: 'absolute',
        bottom: -1,
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: 'rgba(256, 256, 256, 0.12)',
    };

    let backgroundColor = 'transparent';
    if (isOver && canDrop) {
        if (hoverPosition.current === 'middle') {
            backgroundColor = 'rgba(256, 256, 256, 0.12)';
        }
    }

    return (
        <div
            ref={ref}
            className="relative"
            style={{
                opacity: isDragging ? 0.75 : 1,
                backgroundColor: backgroundColor,
            }}
        >
            {isOver && canDrop && hoverPosition.current === 'top' && (
                <div className="absolute" style={separatorTopStyle}></div>
            )}
            {isOver && canDrop && hoverPosition.current === 'bottom' && (
                <div className="absolute" style={separatorBottomStyle}></div>
            )}
            {children}
        </div>
    );
};

export default Layer;
